<template>
    <div class="unified-card-view">
        <!-- 로딩 중 스켈레톤 표시 -->
        <boundary-table-skeleton v-if="loading" />

        <!-- 조직이 없을 때 -->
        <div v-else-if="!hasOrganizations" class="empty-state">
            <i class="fas fa-building"></i>
            <p>조직을 추가해주세요</p>
        </div>

        <!-- 마스터-디테일 레이아웃 -->
        <div v-else class="master-detail-layout">
            <!-- 왼쪽: 조직 목록 (마스터) -->
            <div class="master-panel">
                <div class="master-header">
                    <div class="master-header-left">
                        <h3 class="master-title">조직 목록</h3>
                        <span class="master-count">({{ organizationsList.length }})</span>
                    </div>
                    <IconButton
                        icon="fas fa-plus"
                        size="compact"
                        bordered
                        title="조직 추가"
                        @click.stop="handleAddOrganization"
                    />
                </div>
                <div class="master-list">
                    <div
                        v-for="orgItem in organizationsList"
                        :key="`org-${orgItem.id}`"
                        class="master-item"
                        :class="{ ACTIVE: selectedOrgId === orgItem.id }"
                        @click="selectOrg(orgItem.id)"
                    >
                        <div class="master-item-main">
                            <div class="master-item-header">
                                <div class="master-item-header-left">
                                    <h4 class="master-item-name">{{ orgItem.name }}</h4>
                                    <span class="status-badge-inline" :class="{ ACTIVE: isOrgActive(orgItem.id) }">{{
                                        getStatusLabel(getOrgStatus(orgItem.id))
                                    }}</span>
                                </div>
                                <div class="master-item-header-actions" @click.stop>
                                    <button
                                        class="status-toggle-btn small"
                                        :class="{ ACTIVE: isOrgActive(orgItem.id) }"
                                        @click.stop="toggleOrgStatus(orgItem.id)"
                                        :title="getStatusLabel(getOrgStatus(orgItem.id))"
                                    >
                                        <i
                                            class="fas"
                                            :class="isOrgActive(orgItem.id) ? 'fa-toggle-on' : 'fa-toggle-off'"
                                        ></i>
                                    </button>
                                </div>
                            </div>
                            <div class="master-item-meta">
                                <span v-if="findOrgInfo(orgItem.id).contactPerson" class="meta-text">
                                    <i class="fas fa-user"></i>
                                    {{ findOrgInfo(orgItem.id).contactPerson }}
                                </span>
                                <span v-if="findOrgInfo(orgItem.id).address" class="meta-text">
                                    <i class="fas fa-map-marker-alt"></i>
                                    {{ findOrgInfo(orgItem.id).address }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- 오른쪽: 선택된 조직 상세 정보 (디테일) -->
            <div class="detail-panel">
                <div v-if="!selectedOrgId" class="detail-empty">
                    <i class="fas fa-building"></i>
                    <p>조직을 선택해주세요</p>
                    <small>좌측 목록에서 조직을 선택하면 상세 정보가 표시됩니다.</small>
                </div>

                <div v-else class="detail-content">
                    <div class="detail-header">
                        <div class="detail-header-main">
                            <div class="detail-info-single">
                                <span class="info-value-main org-name" @click="showOrgDetail = true">
                                    {{ selectedOrg.name || selectedOrg.orgName }}
                                    <i class="fas fa-info-circle"></i>
                                </span>
                                <span class="info-separator">&nbsp;&nbsp;&nbsp;/&nbsp;&nbsp;&nbsp;</span>
                                <span class="info-value-main"> {{ selectedOrg.contactPerson || "-" }}</span>
                            </div>
                        </div>
                        <div class="detail-header-actions">
                            <div class="add-menu-wrapper" ref="addMenuWrapper">
                                <IconButton
                                    icon="fas fa-plus"
                                    title="경계 추가"
                                    customClass="add-menu-btn"
                                    @click.stop="toggleAddMenu"
                                />
                                <transition name="dropdown-fade">
                                    <div v-if="showAddMenu" class="add-menu-dropdown">
                                        <button class="add-menu-item" @click.stop="handleAddOrgBoundary">
                                            <i class="fas fa-building"></i>
                                            <span>조직경계 추가</span>
                                        </button>
                                        <button class="add-menu-item" @click.stop="handleAddOperBoundary">
                                            <i class="fas fa-sitemap"></i>
                                            <span>운영경계 추가</span>
                                        </button>
                                    </div>
                                </transition>
                            </div>
                            <IconButton
                                v-if="selectedOrg?.orgIdx"
                                icon="fas fa-paperclip"
                                title="증빙"
                                customClass="info-btn"
                                @click="openEvidenceModal(selectedOrg)"
                            />
                            <IconButton
                                icon="fas fa-edit"
                                title="조직 정보 수정"
                                customClass="info-btn"
                                @click="editOrganization(selectedOrgId)"
                            />
                            <IconButton
                                icon="fas fa-trash"
                                variant="delete"
                                title="삭제"
                                customClass="action-btn delete-btn"
                                @click="deleteOrganization(selectedOrgId)"
                            />
                        </div>
                    </div>

                    <div class="detail-body">
                        <div class="tabs-header-wrapper">
                            <div class="custom-tabs-nav">
                                <button
                                    class="custom-tab-btn"
                                    :class="{ ACTIVE: activeTab === 0 }"
                                    @click="activeTab = 0"
                                >
                                    <span class="tab-title-text">조직경계</span>
                                </button>
                                <button
                                    class="custom-tab-btn"
                                    :class="{ ACTIVE: activeTab === 1 }"
                                    @click="activeTab = 1"
                                >
                                    <span class="tab-title-text">운영경계</span>
                                </button>
                            </div>
                        </div>
                        <div class="tabs-content-wrapper">
                            <div v-if="loadingDetails" class="detail-loading-inner">
                                <i class="fas fa-spinner fa-spin"></i>
                                <span>경계 목록을 불러오는 중...</span>
                            </div>
                            <template v-else>
                                <div v-if="activeTab === 0" class="board-panel">
                                    <div class="board-scroll">
                                        <div v-if="getOrgBoundaries(selectedOrgId).length">
                                            <CommonTable max-height="calc(100vh - 378px)">
                                                <template #header-cells>
                                                    <th>이름</th>
                                                    <th>방법</th>
                                                    <th>시설</th>
                                                    <th>공유율</th>
                                                    <th>기간</th>
                                                    <th class="th-actions">작업</th>
                                                </template>
                                                <template #body>
                                                    <tr
                                                        v-for="boundary in getOrgBoundaries(selectedOrgId)"
                                                        :key="`orgboard-${boundary.id || boundary.orgBoundaryIdx}`"
                                                        class="boundary-table-row"
                                                        @click="openBoundaryDetailModal(boundary, 'org')"
                                                    >
                                                        <td class="td-name">
                                                            {{
                                                                truncateName(
                                                                    boundary.orgBoundaryName ||
                                                                        boundary.remarks ||
                                                                        `조직경계
                                                            ${boundary.orgBoundaryIdx}`,
                                                                )
                                                            }}
                                                        </td>
                                                        <td class="td-method">
                                                            {{ getBoundaryMethodLabel(boundary.accountingMethod) }}
                                                        </td>
                                                        <td class="td-facility">
                                                            {{
                                                                boundary.facilities && boundary.facilities.length
                                                                    ? `${boundary.facilities.length}개`
                                                                    : "미지정"
                                                            }}
                                                        </td>
                                                        <td class="td-share">
                                                            {{ formatSharePct(boundary.sharePct) }}
                                                        </td>
                                                        <td class="td-period">
                                                            {{ formatOrgBoundaryDateRange(boundary) || "기간 미지정" }}
                                                        </td>
                                                        <td class="td-actions" @click.stop>
                                                            <div class="action-buttons">
                                                                <button
                                                                    class="status-toggle-btn small"
                                                                    :class="{ ACTIVE: isOrgBoundaryActive(boundary) }"
                                                                    @click.stop="
                                                                        toggleOrgBoundaryStatus(boundary, selectedOrgId)
                                                                    "
                                                                    :title="
                                                                        getStatusLabel(getOrgBoundaryStatus(boundary))
                                                                    "
                                                                >
                                                                    <i
                                                                        class="fas"
                                                                        :class="
                                                                            isOrgBoundaryActive(boundary)
                                                                                ? 'fa-toggle-on'
                                                                                : 'fa-toggle-off'
                                                                        "
                                                                    ></i>
                                                                </button>
                                                                <IconButton
                                                                    icon="fas fa-edit"
                                                                    size="small"
                                                                    title="편집"
                                                                    @click.stop="
                                                                        editOrgBoundary(boundary, selectedOrgId)
                                                                    "
                                                                />
                                                                <IconButton
                                                                    icon="fas fa-trash"
                                                                    size="small"
                                                                    variant="delete"
                                                                    title="삭제"
                                                                    @click.stop="
                                                                        deleteOrgBoundary(boundary, selectedOrgId)
                                                                    "
                                                                />
                                                            </div>
                                                        </td>
                                                    </tr>
                                                </template>
                                            </CommonTable>
                                        </div>
                                        <div v-else class="board-empty">
                                            <p>조직경계를 추가해주세요</p>
                                        </div>
                                    </div>
                                </div>
                                <div v-else class="board-panel">
                                    <div class="board-scroll">
                                        <div v-if="getOperBoundaries(selectedOrgId).length">
                                            <CommonTable max-height="calc(100vh - 378px)">
                                                <template #header-cells>
                                                    <th>이름</th>
                                                    <th>Scope</th>
                                                    <th>카테고리</th>
                                                    <th>담당자</th>
                                                    <th>상태</th>
                                                    <th>기간</th>
                                                    <th class="th-actions">작업</th>
                                                </template>
                                                <template #body>
                                                    <tr
                                                        v-for="boundary in getOperBoundaries(selectedOrgId)"
                                                        :key="`operboard-${boundary.id || boundary.operBoundaryIdx}`"
                                                        class="boundary-table-row"
                                                        @click="openBoundaryDetailModal(boundary, 'oper')"
                                                    >
                                                        <td class="td-name">
                                                            {{
                                                                truncateName(
                                                                    boundary.operBoundaryName ||
                                                                        boundary.operationalScope ||
                                                                        `운영경계
                                                            ${boundary.operBoundaryIdx}`,
                                                                )
                                                            }}
                                                        </td>
                                                        <td class="td-scope">
                                                            <div class="scope-stack">
                                                                <span
                                                                    v-for="scope in getActiveScopes(boundary)"
                                                                    :key="`${boundary.operBoundaryIdx}-scope-${scope.value}`"
                                                                    class="scope-pill"
                                                                    >{{ scope.label }}
                                                                </span>
                                                                <span
                                                                    v-if="!getActiveScopes(boundary).length"
                                                                    class="scope-pill muted"
                                                                    >없음</span
                                                                >
                                                            </div>
                                                        </td>
                                                        <td class="td-category">
                                                            {{
                                                                getIncludedCategories(boundary.categories).length || 0
                                                            }}개
                                                        </td>
                                                        <td class="td-worker">
                                                            {{ formatWorkerName(boundary.worker) }}
                                                        </td>
                                                        <td class="td-status">
                                                            <span
                                                                class="status-badge"
                                                                :class="{
                                                                    ACTIVE: isOperBoundaryActive(boundary),
                                                                    draft: getOperBoundaryStatus(boundary) === 'DRAFT',
                                                                }"
                                                            >
                                                                {{ getStatusLabel(getOperBoundaryStatus(boundary)) }}
                                                            </span>
                                                        </td>
                                                        <td class="td-period">
                                                            {{ formatOperBoundaryDateRange(boundary) || "기간 미지정" }}
                                                        </td>
                                                        <td class="td-actions" @click.stop>
                                                            <div class="action-buttons">
                                                                <button
                                                                    class="status-toggle-btn small"
                                                                    :class="{ ACTIVE: isOperBoundaryActive(boundary) }"
                                                                    @click.stop="
                                                                        toggleOperBoundaryStatus(
                                                                            boundary,
                                                                            selectedOrgId,
                                                                        )
                                                                    "
                                                                    :title="
                                                                        getStatusLabel(getOperBoundaryStatus(boundary))
                                                                    "
                                                                >
                                                                    <i
                                                                        class="fas"
                                                                        :class="
                                                                            isOperBoundaryActive(boundary)
                                                                                ? 'fa-toggle-on'
                                                                                : 'fa-toggle-off'
                                                                        "
                                                                    ></i>
                                                                </button>
                                                                <IconButton
                                                                    icon="fas fa-edit"
                                                                    size="small"
                                                                    title="편집"
                                                                    @click.stop="
                                                                        editOperBoundary(boundary, selectedOrgId)
                                                                    "
                                                                />
                                                                <IconButton
                                                                    icon="fas fa-trash"
                                                                    size="small"
                                                                    variant="delete"
                                                                    title="삭제"
                                                                    @click.stop="
                                                                        deleteOperBoundary(boundary, selectedOrgId)
                                                                    "
                                                                />
                                                            </div>
                                                        </td>
                                                    </tr>
                                                </template>
                                            </CommonTable>
                                        </div>
                                        <div v-else class="board-empty">
                                            <p>운영경계를 추가해주세요</p>
                                        </div>
                                    </div>
                                </div>
                            </template>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- 조직 상세 정보 모달 -->
        <b-modal
            id="org-detail-modal"
            v-model="showOrgDetail"
            title="조직 정보"
            size="lg"
            centered
            hide-footer
            modal-class="org-modal"
        >
            <div v-if="selectedOrg" class="org-detail-content">
                <div class="org-detail-layout">
                    <div class="org-detail-column">
                        <div class="org-detail-section-compact">
                            <h6 class="detail-section-title-compact">기본 정보</h6>
                            <div class="org-detail-list">
                                <div class="org-detail-row">
                                    <span class="org-detail-label">조직명</span>
                                    <span class="org-detail-value">{{
                                        selectedOrg.name || selectedOrg.orgName || "-"
                                    }}</span>
                                </div>
                                <div class="org-detail-row">
                                    <span class="org-detail-label">조직코드</span>
                                    <span class="org-detail-value">{{ selectedOrg.orgCode || "-" }}</span>
                                </div>
                                <div class="org-detail-row">
                                    <span class="org-detail-label">사업자등록번호</span>
                                    <span class="org-detail-value">{{ selectedOrg.businessNumber || "-" }}</span>
                                </div>
                                <div class="org-detail-row">
                                    <span class="org-detail-label">업종</span>
                                    <span class="org-detail-value">{{ selectedOrg.industry || "-" }}</span>
                                </div>
                                <div class="org-detail-row">
                                    <span class="org-detail-label">주소</span>
                                    <span class="org-detail-value">{{ selectedOrg.address || "-" }}</span>
                                </div>
                                <div class="org-detail-row">
                                    <span class="org-detail-label">회계연도 종료일</span>
                                    <span class="org-detail-value">{{
                                        formatFiscalYearEnd(selectedOrg.fiscalYearEnd) || "-"
                                    }}</span>
                                </div>
                                <div class="org-detail-row">
                                    <span class="org-detail-label">회계 방법</span>
                                    <span class="org-detail-value">{{
                                        getBoundaryMethodLabel(selectedOrg.accountingMethod) || "-"
                                    }}</span>
                                </div>
                                <div class="org-detail-row">
                                    <span class="org-detail-label">상태</span>
                                    <span class="org-detail-value">
                                        <span
                                            class="status-badge"
                                            :class="{
                                                ACTIVE: isOrgActive(selectedOrgId),
                                                draft: getOrgStatus(selectedOrgId) === 'DRAFT',
                                            }"
                                        >
                                            {{ getStatusLabel(getOrgStatus(selectedOrgId)) }}
                                        </span>
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div class="org-detail-section-compact">
                            <h6 class="detail-section-title-compact">담당자 정보</h6>
                            <div class="org-detail-list">
                                <div class="org-detail-row">
                                    <span class="org-detail-label">담당자</span>
                                    <span class="org-detail-value">{{ selectedOrg.contactPerson || "-" }}</span>
                                </div>
                                <div class="org-detail-row">
                                    <span class="org-detail-label">연락처</span>
                                    <span class="org-detail-value">{{ selectedOrg.contactPhone || "-" }}</span>
                                </div>
                                <div class="org-detail-row">
                                    <span class="org-detail-label">이메일</span>
                                    <span class="org-detail-value">{{ selectedOrg.contactEmail || "-" }}</span>
                                </div>
                                <div class="org-detail-row">
                                    <span class="org-detail-label">작업자</span>
                                    <span class="org-detail-value">{{ selectedOrg.worker || "-" }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="org-detail-column">
                        <div class="org-detail-section-compact">
                            <h6 class="detail-section-title-compact">경계 정보</h6>
                            <div class="org-detail-list">
                                <div class="org-detail-row">
                                    <span class="org-detail-label">조직경계</span>
                                    <span class="org-detail-value">{{ getOrgBoundaries(selectedOrgId).length }}개</span>
                                </div>
                                <div class="org-detail-row">
                                    <span class="org-detail-label">운영경계</span>
                                    <span class="org-detail-value"
                                        >{{ getOperBoundaries(selectedOrgId).length }}개</span
                                    >
                                </div>
                                <div class="org-detail-row">
                                    <span class="org-detail-label">시설</span>
                                    <span class="org-detail-value">{{
                                        getOrgFacilityNames(selectedOrgId) || "-"
                                    }}</span>
                                </div>
                            </div>
                        </div>
                        <div v-if="selectedOrg.remarks" class="org-detail-section-compact">
                            <h6 class="detail-section-title-compact">비고</h6>
                            <div class="org-detail-remarks-compact">
                                <p>{{ selectedOrg.remarks }}</p>
                            </div>
                        </div>
                        <div class="org-detail-section-compact">
                            <h6 class="detail-section-title-compact">등록 정보</h6>
                            <div class="org-detail-list">
                                <div class="org-detail-row">
                                    <span class="org-detail-label">등록일</span>
                                    <span class="org-detail-value">{{
                                        formatDateTime(selectedOrg.regDate) || "-"
                                    }}</span>
                                </div>
                                <div class="org-detail-row">
                                    <span class="org-detail-label">수정일</span>
                                    <span class="org-detail-value">{{
                                        formatDateTime(selectedOrg.modDate) || "-"
                                    }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </b-modal>

        <!-- 경계 상세 정보 모달 -->
        <b-modal
            id="boundary-detail-modal"
            v-model="showBoundaryDetailModal"
            :title="selectedBoundaryType === 'org' ? '조직경계 상세 정보' : '운영경계 상세 정보'"
            size="lg"
            centered
            hide-footer
            body-class="boundary-detail-modal-body"
            modal-class="boundary-modal-high-z"
            @hidden="closeBoundaryDetailModal"
        >
            <div v-if="selectedBoundary" class="boundary-detail-modal-content">
                <!-- 조직경계 상세 -->
                <template v-if="selectedBoundaryType === 'org'">
                    <div class="boundary-detail-section">
                        <h5 class="detail-section-title">기본 정보</h5>
                        <div class="detail-grid">
                            <div class="detail-item">
                                <span class="detail-label">이름</span>
                                <span class="detail-value">{{
                                    selectedBoundary.orgBoundaryName ||
                                    selectedBoundary.remarks ||
                                    `조직경계 ${selectedBoundary.orgBoundaryIdx}`
                                }}</span>
                            </div>
                            <div class="detail-item">
                                <span class="detail-label">방법</span>
                                <span class="detail-value">{{
                                    getBoundaryMethodLabel(selectedBoundary.accountingMethod)
                                }}</span>
                            </div>
                            <div class="detail-item">
                                <span class="detail-label">공유율</span>
                                <span class="detail-value">{{ formatSharePct(selectedBoundary.sharePct) }}</span>
                            </div>
                            <div class="detail-item">
                                <span class="detail-label">기간</span>
                                <span class="detail-value">{{
                                    formatOrgBoundaryDateRange(selectedBoundary) || "기간 미지정"
                                }}</span>
                            </div>
                            <div class="detail-item">
                                <span class="detail-label">시설 유형</span>
                                <span class="detail-value">{{
                                    getFacilityTypeSummary(selectedBoundary.facilities)
                                }}</span>
                            </div>
                            <div class="detail-item">
                                <span class="detail-label">상태</span>
                                <span class="detail-value">
                                    <span
                                        class="status-badge"
                                        :class="{
                                            ACTIVE: isOrgBoundaryActive(selectedBoundary),
                                            draft: getOrgBoundaryStatus(selectedBoundary) === 'DRAFT',
                                        }"
                                    >
                                        {{ getStatusLabel(getOrgBoundaryStatus(selectedBoundary)) }}
                                    </span>
                                </span>
                            </div>
                        </div>
                    </div>
                    <div
                        v-if="selectedBoundary.facilities && selectedBoundary.facilities.length"
                        class="boundary-detail-section"
                    >
                        <h5 class="detail-section-title">시설 목록 ({{ selectedBoundary.facilities.length }}개)</h5>
                        <div class="facility-table-wrapper">
                            <table class="facility-table">
                                <thead>
                                    <tr>
                                        <th>이름</th>
                                        <th>코드</th>
                                        <th>유형</th>
                                        <th>주소</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr
                                        v-for="facility in selectedBoundary.facilities"
                                        :key="`facility-${selectedBoundary.orgBoundaryIdx}-${facility.facilityIdx || facility.facilityCode}`"
                                    >
                                        <td class="facility-name-cell">
                                            {{ facility.facilityName || "-" }}
                                        </td>
                                        <td class="facility-code-cell">
                                            {{ facility.facilityCode || "-" }}
                                        </td>
                                        <td class="facility-type-cell">
                                            {{
                                                facility.facilityType
                                                    ? getFacilityTypeLabel(facility.facilityType)
                                                    : "-"
                                            }}
                                        </td>
                                        <td class="facility-address-cell">
                                            <span v-if="facility.address" class="facility-address-text">
                                                {{ facility.address }}
                                            </span>
                                            <span v-else>-</span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div v-if="selectedBoundary.organizationScope" class="boundary-detail-section">
                        <h5 class="detail-section-title">조직 범위 설명</h5>
                        <p class="detail-remarks">{{ selectedBoundary.organizationScope }}</p>
                    </div>
                    <div v-if="selectedBoundary.boundaryRationale" class="boundary-detail-section">
                        <h5 class="detail-section-title">조직 경계 설정 근거</h5>
                        <p class="detail-remarks">{{ selectedBoundary.boundaryRationale }}</p>
                    </div>
                    <div v-if="selectedBoundary.remarks" class="boundary-detail-section">
                        <h5 class="detail-section-title">비고</h5>
                        <p class="detail-remarks">{{ selectedBoundary.remarks }}</p>
                    </div>
                </template>

                <!-- 운영경계 상세 -->
                <template v-else-if="selectedBoundaryType === 'oper'">
                    <div class="boundary-detail-section">
                        <h5 class="detail-section-title">기본 정보</h5>
                        <div class="detail-grid">
                            <div class="detail-item">
                                <span class="detail-label">이름</span>
                                <span class="detail-value">{{
                                    selectedBoundary.operBoundaryName ||
                                    selectedBoundary.operationalScope ||
                                    `운영경계 ${selectedBoundary.operBoundaryIdx}`
                                }}</span>
                            </div>
                            <div class="detail-item">
                                <span class="detail-label">Scope</span>
                                <span class="detail-value">
                                    <div class="scope-stack">
                                        <span
                                            v-for="scope in getActiveScopes(selectedBoundary)"
                                            :key="`scope-${scope.value}`"
                                            class="scope-pill"
                                            >{{ scope.label }}</span
                                        >
                                        <span v-if="!getActiveScopes(selectedBoundary).length" class="scope-pill muted"
                                            >없음</span
                                        >
                                    </div>
                                </span>
                            </div>
                            <div class="detail-item">
                                <span class="detail-label">담당자</span>
                                <span class="detail-value">{{ selectedBoundary.worker || "-" }}</span>
                            </div>
                            <div class="detail-item">
                                <span class="detail-label">상태</span>
                                <span class="detail-value">
                                    <span
                                        class="status-badge"
                                        :class="{
                                            ACTIVE: isOperBoundaryActive(selectedBoundary),
                                            draft: getOperBoundaryStatus(selectedBoundary) === 'DRAFT',
                                        }"
                                    >
                                        {{ getStatusLabel(getOperBoundaryStatus(selectedBoundary)) }}
                                    </span>
                                </span>
                            </div>
                        </div>
                    </div>
                    <div
                        v-if="getIncludedCategories(selectedBoundary.categories).length"
                        class="boundary-detail-section"
                    >
                        <h5 class="detail-section-title">
                            카테고리 목록 ({{ getIncludedCategories(selectedBoundary.categories).length }}개)
                        </h5>
                        <div class="category-list-modal">
                            <span
                                v-for="category in getIncludedCategories(selectedBoundary.categories)"
                                :key="`category-${category.categoryCode}`"
                                class="category-chip-modal"
                            >
                                {{ category.categoryName }}
                            </span>
                        </div>
                    </div>
                    <div class="boundary-detail-section">
                        <h5 class="detail-section-title">운영 경계 설명</h5>
                        <p class="detail-remarks">{{ selectedBoundary.scopeRationale || "설명 없음" }}</p>
                    </div>

                    <div class="boundary-detail-section">
                        <h5 class="detail-section-title">운영 경계 범위</h5>
                        <p class="detail-remarks">{{ selectedBoundary.operationalScope || "범위 내용 없음" }}</p>
                    </div>
                    <div class="boundary-detail-section">
                        <h5 class="detail-section-title">비고</h5>
                        <p class="detail-remarks">{{ selectedBoundary.remarks || "비고 없음" }}</p>
                    </div>
                </template>
            </div>
        </b-modal>
    </div>
</template>

<script>
import carbonApi from "../../../api";
import { unwrapSuccess } from "../../../utils/api.js";
import CarbonCodes from "../../../codes";
import BoundaryTableSkeleton from "./BoundaryTableSkeleton.vue";
import CommonTable from "../../common/CommonTable.vue";
import IconButton from "../../common/IconButton.vue";

export default {
    name: "BoundaryTreeTable",
    components: {
        BoundaryTableSkeleton,
        CommonTable,
        IconButton,
    },
    props: {
        loading: {
            type: Boolean,
            default: false,
        },
        loadingDetails: {
            type: Boolean,
            default: false,
        },
        organizations: {
            type: Object,
            required: true,
        },
        organizationsList: {
            type: Array,
            required: true,
        },
    },
    data() {
        return {
            selectedOrgId: null,
            facilityPreviewLimit: 4,
            categoryPreviewLimit: 5,
            expandedBoundaryDetails: {},
            expandedOrgBoundarySections: {},
            expandedOperSections: {},
            showBoundaryDetailModal: false,
            selectedBoundary: null,
            selectedBoundaryType: null, // 'org' or 'oper'
            showOrgDetail: false,
            showAddMenu: false,
            activeTab: 0, // 0: 조직경계, 1: 운영경계
        };
    },
    computed: {
        hasOrganizations() {
            return this.organizationsList && this.organizationsList.length > 0;
        },

        selectedOrg() {
            if (!this.selectedOrgId) return {};
            return this.getOrganization(this.selectedOrgId) || {};
        },
    },
    mounted() {
        document.addEventListener("click", this.handleClickOutside);
    },
    beforeDestroy() {
        document.removeEventListener("click", this.handleClickOutside);
    },
    methods: {
        findOrgInfo(orgId) {
            if (!this.organizations || !this.organizations[orgId]) return {};
            return this.organizations[orgId];
        },
        getOrganization(orgId) {
            if (!this.organizations || !this.organizations[orgId]) return {};
            return this.organizations[orgId];
        },

        getOrgBoundaries(orgId) {
            if (!this.organizations || !this.organizations[orgId]) return [];
            return this.organizations[orgId].orgBoundaries || [];
        },

        getOperBoundaries(orgId) {
            if (!this.organizations || !this.organizations[orgId]) return [];
            return this.organizations[orgId].operBoundaries || [];
        },

        selectOrg(orgId) {
            if (this.selectedOrgId === orgId) {
                this.selectedOrgId = null;
            } else {
                this.selectedOrgId = orgId;
                // 부모에서 해당 조직의 경계 데이터 로드 (액션 시점 API 호출)
                this.$emit("select-org", orgId);
                // 선택 시 조직경계와 운영경계 섹션 자동 확장
                if (this.expandedOrgBoundarySections[orgId] === undefined) {
                    this.$set(this.expandedOrgBoundarySections, orgId, true);
                }
                if (this.expandedOperSections[orgId] === undefined) {
                    this.$set(this.expandedOperSections, orgId, true);
                }
            }
        },

        toggleBoundaryDetail(boundary) {
            const key = boundary.id || boundary.orgBoundaryIdx || boundary.operBoundaryIdx;
            if (!key && key !== 0) return;
            this.$set(this.expandedBoundaryDetails, key, !this.expandedBoundaryDetails[key]);
        },

        isBoundaryExpanded(boundary) {
            const key = boundary.id || boundary.orgBoundaryIdx || boundary.operBoundaryIdx;
            if (!key && key !== 0) return false;
            return !!this.expandedBoundaryDetails[key];
        },

        // 조직 추가 메서드
        handleAddOrganization() {
            this.$emit("add-organization");
        },

        // 편집 메서드
        openEvidenceModal(org) {
            if (!org?.orgIdx) return;
            this.$emit("evidence-organization", { org });
        },
        editOrganization(orgId) {
            const org = this.getOrganization(orgId);
            this.$emit("edit-organization", { orgId, orgData: org });
        },

        editOrgBoundary(boundary, orgId) {
            this.$emit("edit-org-boundary", { boundary, orgId });
        },

        editOperBoundary(boundary, orgId) {
            this.$emit("edit-oper-boundary", { boundary, orgId });
        },

        // 경계 추가 메서드
        addOrgBoundary(orgId) {
            this.$emit("add-org-boundary", { orgId });
        },

        addOperBoundary(orgId) {
            this.$emit("add-oper-boundary", { orgId });
        },

        toggleAddMenu() {
            this.showAddMenu = !this.showAddMenu;
        },

        closeAddMenu() {
            this.showAddMenu = false;
        },

        handleAddOrgBoundary() {
            this.addOrgBoundary(this.selectedOrgId);
            this.closeAddMenu();
        },

        handleAddOperBoundary() {
            this.addOperBoundary(this.selectedOrgId);
            this.closeAddMenu();
        },

        handleClickOutside(event) {
            if (this.showAddMenu && this.$refs.addMenuWrapper) {
                if (!this.$refs.addMenuWrapper.contains(event.target)) {
                    this.closeAddMenu();
                }
            }
        },

        // 삭제 메서드
        async deleteOrganization(orgId) {
            const org = this.getOrganization(orgId);
            const confirm = await this.alertConfirmWarning(
                `조직 '${org.orgName || org.name}'을(를) 삭제하시겠습니까?\n\n관련된 모든 조직경계, 운영경계, 시설 매핑이 함께 삭제됩니다.`,
            );

            if (!confirm.value) return;

            try {
                const orgCode = org.orgCode;
                const response = await carbonApi.organization.deleteOrg(orgCode);

                // 삭제 성공 여부 확인
                const responseData = response?.data || response;
                if (responseData.success === false) {
                    this.$emit("delete-error", { type: "org", error: { response: { data: responseData } } });
                    return;
                }

                const message = responseData.message || "조직이 삭제되었습니다.";
                this.$emit("organization-deleted", { orgId, orgCode, message });
            } catch (error) {
                console.error("[BoundaryTreeTable] 조직 삭제 실패:", error);
                this.$emit("delete-error", { type: "org", error });
            }
        },

        async deleteOrgBoundary(boundary, orgId) {
            const confirm = await this.alertConfirmWarning(
                `조직경계 '${boundary.orgBoundaryName || "이름 없음"}'을(를) 삭제하시겠습니까?\n\n관련된 모든 시설 매핑이 함께 삭제됩니다.`,
            );

            if (!confirm.value) return;

            try {
                const boundaryIdx = boundary.orgBoundaryIdx || boundary.boundaryIdx;
                const response = await carbonApi.orgBoundary.deleteOrgBoundary(boundaryIdx);

                // 삭제 성공 여부 확인
                const responseData = response?.data || response;
                if (responseData.success === false) {
                    this.$emit("delete-error", { type: "orgBoundary", error: { response: { data: responseData } } });
                    return;
                }

                const message = responseData.message || "조직경계가 삭제되었습니다.";
                this.$emit("org-boundary-deleted", { boundaryIdx, orgId, message });
            } catch (error) {
                console.error("[BoundaryTreeTable] 조직경계 삭제 실패:", error);
                this.$emit("delete-error", { type: "orgBoundary", error });
            }
        },

        async deleteOperBoundary(boundary, orgId) {
            const confirm = await this.alertConfirmWarning(
                `운영경계 '${boundary.operBoundaryName || "이름 없음"}'을(를) 삭제하시겠습니까?\n\n관련된 모든 카테고리 매핑이 함께 삭제됩니다.`,
            );

            if (!confirm.value) return;

            try {
                const operBoundaryIdx = boundary.operBoundaryIdx;
                const response = await carbonApi.operBoundary.deleteOperBoundary(operBoundaryIdx);

                // 삭제 성공 여부 확인
                const responseData = response?.data || response;
                if (responseData.success === false) {
                    this.$emit("delete-error", { type: "operBoundary", error: { response: { data: responseData } } });
                    return;
                }

                const message = responseData.message || "운영경계가 삭제되었습니다.";
                this.$emit("oper-boundary-deleted", { operBoundaryIdx, orgId, message });
            } catch (error) {
                console.error("[BoundaryTreeTable] 운영경계 삭제 실패:", error);
                this.$emit("delete-error", { type: "operBoundary", error });
            }
        },

        getCategoriesByScope(categories, scopeType) {
            if (!categories || categories.length === 0) return [];
            return categories.filter((c) => {
                if (!c) return false;
                const isIncluded = c.included === true || c.included === 1 || c.included === "1";
                return c.scopeType === scopeType && isIncluded;
            });
        },

        getIncludedCategoryNames(categories) {
            if (!categories || categories.length === 0) return "";

            const includedCategories = categories.filter((c) => {
                if (!c) return false;
                return c.included === true || c.included === 1 || c.included === "1";
            });

            return includedCategories
                .map((c) => c.categoryName || c.categoryCode || "")
                .filter(Boolean)
                .join(", ");
        },

        getIncludedCategories(categories) {
            if (!categories || categories.length === 0) return [];
            return categories.filter((c) => {
                if (!c) return false;
                return c.included === true || c.included === 1 || c.included === "1";
            });
        },

        getActiveScopes(boundary) {
            if (!boundary) return [];

            // categories 배열에서 scopeType 추출
            if (boundary.categories && Array.isArray(boundary.categories) && boundary.categories.length > 0) {
                const includedCategories = boundary.categories.filter((c) => {
                    if (!c) return false;
                    return c.included === true || c.included === 1 || c.included === "1";
                });

                // 고유한 scopeType 추출
                const scopeSet = new Set();
                includedCategories.forEach((category) => {
                    if (category.scopeType) {
                        scopeSet.add(category.scopeType.toLowerCase());
                    }
                });

                // scopeType을 CarbonCodes로 라벨 변환
                const scopes = Array.from(scopeSet)
                    .map((scopeValue) => ({
                        value: scopeValue,
                        label: CarbonCodes.scopeType.getLabel(scopeValue),
                    }))
                    .filter((s) => s.label && s.label !== "-")
                    .sort((a, b) => a.value.localeCompare(b.value));

                return scopes;
            }

            // categories가 없거나 비어있으면 기존 방식으로 fallback
            const scopes = [];
            if (boundary.scope1 === 1 || boundary.scope1 === true) {
                scopes.push({ value: "SCOPE1", label: CarbonCodes.scopeType.getLabel("SCOPE1") });
            }
            if (boundary.scope2 === 1 || boundary.scope2 === true) {
                scopes.push({ value: "SCOPE2", label: CarbonCodes.scopeType.getLabel("SCOPE2") });
            }
            if (boundary.scope3 === 1 || boundary.scope3 === true) {
                scopes.push({ value: "SCOPE3", label: CarbonCodes.scopeType.getLabel("SCOPE3") });
            }
            return scopes;
        },

        formatOrgBoundaryDateRange(boundary) {
            if (!boundary) return "";
            const start = this.formatDate(boundary.startDate);
            const end = this.formatDate(boundary.endDate);
            if (start && end) {
                return `${start} ~ ${end}`;
            }
            if (start) {
                return `${start} ~`;
            }
            if (end) {
                return `~ ${end}`;
            }
            return "";
        },

        formatOperBoundaryDateRange(boundary) {
            if (!boundary) return "";
            const start = this.formatDate(boundary.startDate);
            const end = this.formatDate(boundary.endDate);
            if (start && end) {
                return `${start} ~ ${end}`;
            }
            if (start) {
                return `${start} ~`;
            }
            if (end) {
                return `~ ${end}`;
            }
            return "";
        },

        formatDate(value) {
            if (!value) return "";
            const datePart = typeof value === "string" ? value.split(" ")[0] : value;
            return datePart ? datePart.replace(/-/g, ".") : "";
        },

        getOrgBoundarySummary(orgId) {
            const orgCount = this.getOrgBoundaries(orgId).length;
            const operCount = this.getOperBoundaries(orgId).length;
            return `조직경계 ${orgCount}개 · 운영경계 ${operCount}개`;
        },

        getOrgShareDisplay(orgId) {
            const facilityCount = this.getOrgFacilityCount(orgId);
            if (facilityCount > 0) {
                return `시설 ${facilityCount}개`;
            }
            return "지분율 -";
        },

        getOrgFacilityCount(orgId) {
            const boundaries = this.getOrgBoundaries(orgId);
            let total = 0;
            boundaries.forEach((boundary) => {
                if (boundary && boundary.facilities) {
                    total += boundary.facilities.length;
                }
            });
            return total;
        },

        getOrgFacilityTypeSummary(orgId) {
            const boundaries = this.getOrgBoundaries(orgId);
            const typeSet = new Set();
            boundaries.forEach((boundary) => {
                if (boundary && boundary.facilities) {
                    boundary.facilities.forEach((facility) => {
                        if (facility && facility.facilityType) {
                            typeSet.add(facility.facilityType);
                        }
                    });
                }
            });
            return this.formatTypeSet(typeSet);
        },

        getOrgFacilityNames(orgId) {
            const boundaries = this.getOrgBoundaries(orgId);
            const facilityNames = [];
            boundaries.forEach((boundary) => {
                if (boundary && boundary.facilities) {
                    boundary.facilities.forEach((facility) => {
                        if (facility && facility.facilityName) {
                            facilityNames.push(facility.facilityName);
                        }
                    });
                }
            });
            if (facilityNames.length === 0) return "-";
            // 중복 제거
            const uniqueNames = [...new Set(facilityNames)];
            return uniqueNames.join(", ");
        },

        getFacilityTypeSummary(facilities) {
            if (!facilities || facilities.length === 0) return "-";
            const typeSet = new Set();
            facilities.forEach((facility) => {
                if (facility && facility.facilityType) {
                    typeSet.add(facility.facilityType);
                }
            });
            return this.formatTypeSet(typeSet);
        },

        formatTypeSet(typeSet) {
            if (!typeSet || typeSet.size === 0) return "-";
            const types = Array.from(typeSet);
            const facilityTypes = CarbonCodes.facilityType.getList();
            const typeLabels = types.map((typeValue) => {
                const type = facilityTypes.find((t) => t.value === typeValue);
                return type ? type.text : typeValue;
            });
            if (typeLabels.length <= 2) {
                return typeLabels.join(", ");
            }
            return `${typeLabels.slice(0, 2).join(", ")} 외 ${typeLabels.length - 2}`;
        },

        formatSharePct(value) {
            if (value === null || value === undefined || value === "") {
                return "-";
            }
            const parsed = Number(value);
            if (Number.isNaN(parsed)) {
                return "-";
            }
            return `${parsed}%`;
        },

        getBoundaryMethodLabel(value) {
            return CarbonCodes.accountingMethod.getLabel(value);
        },

        getFacilityTypeLabel(value) {
            return CarbonCodes.facilityType.getLabel(value);
        },

        getFacilityNamesPreview(facilities) {
            if (!facilities || !facilities.length) return "";
            const names = facilities.map((facility) => facility.facilityName || facility.facilityCode).filter(Boolean);
            if (!names.length) return "";
            const limit = 3;
            const preview = names.slice(0, limit).join(", ");
            const remaining = names.length - limit;
            return remaining > 0 ? `${preview} 외 ${remaining}개 시설` : preview;
        },

        getFacilityNamesFullList(facilities) {
            if (!facilities || !facilities.length) return "";
            return facilities
                .map((facility) => facility.facilityName || facility.facilityCode)
                .filter(Boolean)
                .join(", ");
        },

        getFacilityPreviewList(boundary) {
            if (!boundary || !boundary.facilities) return [];
            return boundary.facilities.slice(0, this.facilityPreviewLimit);
        },

        getCategoryNamesPreview(categories) {
            if (!categories || !categories.length) return "";
            const names = categories.map((category) => category.categoryName || category.categoryCode).filter(Boolean);
            if (!names.length) return "";
            const limit = 4;
            const preview = names.slice(0, limit).join(", ");
            const remaining = names.length - limit;
            return remaining > 0 ? `${preview} 외 ${remaining}개 카테고리` : preview;
        },

        getCategoryNamesFullList(categories) {
            if (!categories || !categories.length) return "";
            return categories
                .map((category) => category.categoryName || category.categoryCode)
                .filter(Boolean)
                .join(", ");
        },

        formatWorkerName(name) {
            if (!name) return "-";
            return name.length > 6 ? `${name.slice(0, 6)}…` : name;
        },

        truncateName(name) {
            const maxLength = 13;
            if (!name) return "-";
            return name.length > maxLength ? `${name.slice(0, maxLength)}...` : name;
        },

        getCategoryPreviewList(boundary) {
            if (!boundary) return [];
            const included = this.getIncludedCategories(boundary.categories);
            return included.slice(0, this.categoryPreviewLimit);
        },

        formatContactFull(org) {
            if (!org) return "-";

            const parts = [];

            if (org.contactPerson) {
                parts.push(org.contactPerson);
            }

            if (org.contactEmail) {
                parts.push(org.contactEmail);
            }

            if (org.contactPhone) {
                parts.push(org.contactPhone);
            }

            return parts.length > 0 ? parts.join(", ") : "-";
        },

        formatDateTime(dateTimeStr) {
            if (!dateTimeStr) return "-";
            try {
                const date = new Date(dateTimeStr);
                if (isNaN(date.getTime())) return dateTimeStr;
                return date.toLocaleString("ko-KR", {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                });
            } catch (e) {
                return dateTimeStr;
            }
        },

        formatFiscalYearEnd(value) {
            if (!value) return "-";
            // "1231" 형식을 "12월 31일"로 변환
            if (typeof value === "string" && value.length === 4) {
                const month = value.substring(0, 2);
                const day = value.substring(2, 4);
                return `${parseInt(month)}월 ${parseInt(day)}일`;
            }
            return value;
        },

        openBoundaryDetailModal(boundary, type) {
            this.selectedBoundary = boundary;
            this.selectedBoundaryType = type;
            this.showBoundaryDetailModal = true;
        },

        closeBoundaryDetailModal() {
            this.showBoundaryDetailModal = false;
            this.selectedBoundary = null;
            this.selectedBoundaryType = null;
        },

        getStatusLabel(status) {
            return CarbonCodes.draftStat.getLabel(status);
        },

        // 조직 상태 값 가져오기
        getOrgStatus(orgId) {
            const org = this.getOrganization(orgId);
            return org && org.status ? org.status : "ACTIVE";
        },

        // 조직경계 상태 값 가져오기
        getOrgBoundaryStatus(boundary) {
            return boundary && boundary.draftStat ? boundary.draftStat : "ACTIVE";
        },

        // 운영경계 상태 값 가져오기
        getOperBoundaryStatus(boundary) {
            return boundary && boundary.draftStat ? boundary.draftStat : "ACTIVE";
        },

        // 조직 활성화 여부
        isOrgActive(orgId) {
            return this.getOrgStatus(orgId) === "ACTIVE";
        },

        // 조직경계 활성화 여부
        isOrgBoundaryActive(boundary) {
            return this.getOrgBoundaryStatus(boundary) === "ACTIVE";
        },

        // 운영경계 활성화 여부
        isOperBoundaryActive(boundary) {
            return this.getOperBoundaryStatus(boundary) === "ACTIVE";
        },

        // 조직 상태 토글
        async toggleOrgStatus(orgId) {
            const org = this.getOrganization(orgId);
            const orgIdx = org.orgIdx;
            if (!orgIdx) return;

            const currentStatus = this.isOrgActive(orgId);
            const nextStatus = currentStatus ? "INACTIVE" : "ACTIVE";

            try {
                // 낙관적 업데이트 (Optimistic Update)
                this.$emit("update-org-status-local", { orgId, status: nextStatus });

                // API 호출 (백그라운드)
                const responseData = unwrapSuccess(
                    await carbonApi.organization.updateOrg(orgIdx, { status: nextStatus }),
                    "조직 상태 변경에 실패했습니다.",
                );

                this.$emit("org-updated", {
                    orgId,
                    orgData: responseData,
                });
            } catch (error) {
                console.error("[BoundaryTreeTable] 조직 상태 변경 실패:", error);

                // 실패 시 롤백
                const rollbackStatus = currentStatus ? "ACTIVE" : "INACTIVE";
                this.$emit("update-org-status-local", { orgId, status: rollbackStatus });
                this.$emit("status-toggle-error", error);
            }
        },

        // 조직경계 상태 토글
        async toggleOrgBoundaryStatus(boundary, orgId) {
            const boundaryIdx = boundary.orgBoundaryIdx || boundary.boundaryIdx;
            if (!boundaryIdx) return;

            const currentStatus = this.isOrgBoundaryActive(boundary);
            const nextStatus = currentStatus ? "INACTIVE" : "ACTIVE";

            try {
                // 낙관적 업데이트
                this.$emit("update-boundary-status-local", {
                    type: "org",
                    boundaryId: boundary.id,
                    orgId,
                    status: nextStatus,
                });

                // API 호출
                await unwrapSuccess(
                    await carbonApi.orgBoundary.setOrgBoundaryStatus(boundaryIdx, nextStatus),
                    "조직경계 상태 변경에 실패했습니다.",
                );

                this.$emit("boundary-status-changed", {
                    type: "org",
                    boundaryIdx,
                    orgId,
                    status: nextStatus,
                });
            } catch (error) {
                console.error("[BoundaryTreeTable] 조직경계 상태 변경 실패:", error);

                // 실패 시 롤백
                const rollbackStatus = currentStatus ? "ACTIVE" : "INACTIVE";
                this.$emit("update-boundary-status-local", {
                    type: "org",
                    boundaryId: boundary.id,
                    orgId,
                    status: rollbackStatus,
                });
                this.$emit("status-toggle-error", error);
            }
        },

        // 운영경계 상태 토글
        async toggleOperBoundaryStatus(boundary, orgId) {
            const operBoundaryIdx = boundary.operBoundaryIdx;
            if (!operBoundaryIdx) return;

            const currentStatus = this.isOperBoundaryActive(boundary);
            const nextStatus = currentStatus ? "INACTIVE" : "ACTIVE";

            try {
                // 낙관적 업데이트
                this.$emit("update-boundary-status-local", {
                    type: "oper",
                    boundaryId: boundary.id,
                    orgId,
                    status: nextStatus,
                });

                // API 호출
                await unwrapSuccess(
                    await carbonApi.operBoundary.setOperBoundaryStatus(operBoundaryIdx, nextStatus),
                    "운영경계 상태 변경에 실패했습니다.",
                );

                this.$emit("boundary-status-changed", {
                    type: "oper",
                    boundaryIdx: operBoundaryIdx,
                    orgId,
                    status: nextStatus,
                });
            } catch (error) {
                console.error("[BoundaryTreeTable] 운영경계 상태 변경 실패:", error);

                // 실패 시 롤백
                const rollbackStatus = currentStatus ? "ACTIVE" : "INACTIVE";
                this.$emit("update-boundary-status-local", {
                    type: "oper",
                    boundaryId: boundary.id,
                    orgId,
                    status: rollbackStatus,
                });
                this.$emit("status-toggle-error", error);
            }
        },
    },
};
</script>

<style scoped>
.boundary-table-row {
    font-weight: 500;
    cursor: pointer;
}
.info-separator {
    font-weight: 900;
    font-size: 12px;
}
/* 통합 카드 뷰 */
.unified-card-view {
    background: #ffffff;
    border-radius: 10px;
    height: 100%;
}

/* 마스터-디테일 레이아웃 */
.master-detail-layout {
    display: flex;
    height: 100%;
    /* min-height: 600px; */
    gap: 0;
    border-bottom: 2px solid #e2e8f0;
}

/* 마스터 패널 (왼쪽) */
.master-panel {
    width: 320px;
    min-width: 280px;
    background: #f8fafc;
    border-right: 1px solid #e2e8f0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.master-header {
    padding: 14px 16px;
    background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
}

.master-header-left {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
}

.master-title {
    margin: 0;
    font-size: 14px;
    font-weight: 700;
    color: #1e293b;
}

.master-count {
    font-size: 12px;
    color: #64748b;
    font-weight: 500;
}

.master-list {
    flex: 1;
    overflow-y: auto;
    padding: 6px;
    max-height: 510px;
}

.master-item {
    padding: 8px 10px;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    margin-bottom: 6px;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
}

.master-item:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);
}

.master-item.ACTIVE {
    background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
    border-color: #86efac;
    box-shadow: 0 2px 6px rgba(34, 197, 94, 0.12);
}

.master-item-main {
    flex: 1;
    min-width: 0;
}

.master-item-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 6px;
}

.master-item-header-left {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
    min-width: 0;
}

.master-item-header-actions {
    flex-shrink: 0;
}

.master-item-name {
    margin: 0;
    font-size: 13px;
    font-weight: 600;
    color: #0f172a;
    letter-spacing: -0.01em;
    flex: 1;
}

.master-item.ACTIVE .master-item-name {
    color: #15803d;
}

.status-badge-inactive {
    display: inline-block;
    padding: 2px 6px;
    font-size: 10px;
    font-weight: 500;
    color: #94a3b8;
    background: #f1f5f9;
    border-radius: 4px;
    border: 1px solid #e2e8f0;
}

.status-badge-inline {
    display: inline-block;
    padding: 2px 6px;
    font-size: 10px;
    font-weight: 600;
    color: #64748b;
    background: #f1f5f9;
    border-radius: 4px;
    border: 1px solid #e2e8f0;
}

.status-badge-inline.ACTIVE {
    color: #059669;
    background: #ecfdf5;
    border-color: #86efac;
}

.master-item-info {
    display: none;
    border-bottom: 1px solid #f1f5f9;
}

.info-row {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 11px;
}

.info-label {
    font-weight: 500;
    color: #64748b;
    min-width: 32px;
    flex-shrink: 0;
}

.info-value {
    font-weight: 500;
    color: #1e293b;
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.master-item.ACTIVE .info-value {
    color: #334155;
}

.master-item-meta {
    display: flex;
    flex-direction: row;
    gap: 8px;
    flex-wrap: wrap;
}

.meta-text {
    font-size: 11px;
    color: #64748b;
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    gap: 4px;
}

.meta-text i {
    font-size: 10px;
    color: #94a3b8;
}

.master-item.ACTIVE .meta-text {
    color: #475569;
}

.master-item.ACTIVE .meta-text i {
    color: #64748b;
}

/* 디테일 패널 (오른쪽) */
.detail-panel {
    flex: 1;
    display: flex;
    flex-direction: column;
    background: #ffffff;
    overflow: hidden;
}

.detail-empty {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    /* padding: 48px 20px; */
    color: #64748b;
    text-align: center;
}

.detail-empty i {
    font-size: 44px;
    margin-bottom: 12px;
    color: #94a3b8;
}

.detail-empty p {
    margin: 0 0 6px 0;
    font-size: 14px;
    font-weight: 500;
    color: #475569;
}

.detail-empty small {
    font-size: 12px;
    color: #64748b;
}

.detail-loading-inner {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 48px 20px;
    color: #64748b;
    font-size: 14px;
}

.detail-loading-inner i {
    font-size: 20px;
}

.detail-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
}

.detail-header {
    padding: 10px 20px 0px 20px;
    background: #ffffff;
    /* border-bottom: 1px solid #e2e8f0; */
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    flex-shrink: 0;
}

.detail-header-main {
    flex: 1;
    min-width: 0;
}

.detail-info-row {
    display: flex;
    align-items: center;
    gap: 32px;
    flex-wrap: wrap;
}

.info-group {
    display: flex;
    align-items: center;
    gap: 12px;
}

.info-group .info-label {
    font-size: 11px;
    font-weight: 600;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    min-width: 50px;
    flex-shrink: 0;
}

.info-values {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
}

.info-value-main {
    font-size: 13px;
    font-weight: 600;
    color: #059669;
}

.info-value-sub {
    font-size: 12px;
    color: #64748b;
    font-weight: 400;
}

.org-name {
    cursor: pointer;
    color: #059669;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    transition: color 0.2s ease;
}

.org-name:hover {
    color: #047857;
}

.org-name i {
    font-size: 11px;
    color: #94a3b8;
}

.org-name:hover i {
    color: #64748b;
}

.detail-header-actions {
    display: flex;
    gap: 6px;
    align-items: center;
    flex-shrink: 0;
}

.add-menu-wrapper {
    position: relative;
}

.add-menu-wrapper ::v-deep .add-menu-btn {
    font-size: 16px;
}

.add-menu-dropdown {
    position: absolute;
    top: 100%;
    right: 0;
    margin-top: 8px;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    z-index: 100;
    min-width: 160px;
    overflow: hidden;
}

.dropdown-fade-enter-ACTIVE,
.dropdown-fade-leave-ACTIVE {
    transition: all 0.2s ease;
}

.dropdown-fade-enter,
.dropdown-fade-leave-to {
    opacity: 0;
    transform: translateY(-8px);
}

.add-menu-item {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 10px 14px;
    background: #ffffff;
    border: none;
    text-align: left;
    font-size: 13px;
    font-weight: 500;
    color: #1e293b;
    cursor: pointer;
    transition: background 0.15s ease;
}

.add-menu-item:hover {
    background: #f8fafc;
}

.add-menu-item:first-child {
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
}

.add-menu-item:last-child {
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
}

.add-menu-item i {
    font-size: 12px;
    color: #64748b;
    width: 16px;
    text-align: center;
}

/* IconButton에 customClass로 적용 시 자식 루트에 스타일 전달 */
.detail-header-actions ::v-deep .info-btn,
.detail-header-actions ::v-deep .action-btn {
    width: 28px;
    height: 28px;
    font-size: 16px;
}

.detail-body {
    flex: 1;
    padding: 0;
    background: #ffffff;
    display: flex;
    flex-direction: column;
}

/* 탭 스타일 */
.tabs-header-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0px 20px;
    border-bottom: 1px solid #e2e8f0;
    gap: 16px;
    min-height: 44px;
    position: relative;
}

.custom-tabs-nav {
    display: flex;
    gap: 8px;
    flex: 1;
    align-items: flex-end;
}

.custom-tab-btn {
    padding: 6px 16px;
    background: transparent;
    border: none;
    border-bottom: 2px solid transparent;
    border-radius: 0;
    font-size: 12px;
    font-weight: 600;
    color: #64748b;
    cursor: pointer;
    transition: all 0.2s ease;
    position: relative;
}

.custom-tab-btn::after {
    content: "";
    position: absolute;
    bottom: -8px;
    left: 0;
    right: 0;
    height: 2px;
    background: transparent;
    transition: background 0.2s ease;
}

.custom-tab-btn.ACTIVE::after {
    background: #059669;
}

.custom-tab-btn:hover {
    background: transparent;
    color: #475569;
}

.custom-tab-btn.ACTIVE {
    background: transparent;
    color: #059669;
}

.tabs-content-wrapper {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
}

.tab-add-btn {
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    color: #64748b;
    font-size: 11px;
    cursor: pointer;
    transition: all 0.2s ease;
    flex-shrink: 0;
    margin-bottom: 2px;
}

.tab-add-btn:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
    color: #475569;
}

.tab-add-btn:active {
    transform: scale(0.95);
}

.tab-title-text {
    font-weight: 600;
    margin-right: 5px;
}

.tab-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    font-size: 11px;
    font-weight: 600;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 10px;
    line-height: 1;
}

.board-panel {
    background: #ffffff;
    border: none;
    border-radius: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    min-height: 0;
    flex: 1;
}

.board-header-compact {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    border-bottom: none;
    padding: 10px;
    background: #fafbfc;
}

.ghost-btn.compact {
    width: 32px;
    height: 32px;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    border: 1px solid #86efac;
    background: #ecfdf5;
    color: #059669;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 1px 2px rgba(5, 150, 105, 0.1);
}

.ghost-btn.compact:hover {
    background: #059669;
    color: #ffffff;
    border-color: #059669;
    box-shadow: 0 4px 8px rgba(5, 150, 105, 0.2);
    transform: translateY(-1px);
}

.ghost-btn.compact:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(5, 150, 105, 0.15);
}

.board-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
}

.board-eyebrow {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: #94a3b8;
    margin: 0 0 4px 0;
}

.board-title {
    margin: 0;
    font-size: 18px;
    font-weight: 700;
    color: #0f172a;
}

.board-header-actions {
    display: flex;
    align-items: center;
    gap: 10px;
    justify-content: flex-end;
    width: 40%;
}

.board-count {
    font-size: 12px;
    font-weight: 600;
    color: #475569;
}

.ghost-btn {
    border: 1px solid #e2e8f0;
    background: transparent;
    color: #0f172a;
    padding: 6px 12px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    transition: all 0.2s ease;
}

.ghost-btn:hover {
    border-color: #cbd5e1;
    box-shadow: 0 2px 6px rgba(15, 23, 42, 0.08);
}

.ghost-btn:active,
.ghost-btn:focus {
    outline: none;
    border: 1px solid #e2e8f0;
}

.ghost-btn.compact:active,
.ghost-btn.compact:focus {
    outline: none;
    border: none;
    border-bottom: 1.5px solid #e2e8f0;
}

.board-scroll {
    flex: 1;
    padding: 0;
    min-height: 0;
    display: flex;
    flex-direction: column;
    max-height: calc(100vh - 300px);
}

.boundary-table-wrapper {
    width: 98%;
    background: #ffffff;
    position: relative;
    border: 1px solid #e5e7eb;
    border-radius: 14px;
    margin: 12px;
    max-height: calc(100vh - 378px);
    overflow-y: auto;
    box-shadow: 0 10px 24px rgba(15, 23, 42, 0.04);
}

.boundary-detail-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    background: #ffffff;
    font-size: 12.5px;
    color: #0f172a;
}

.boundary-detail-table thead {
    background: #f8fafc;
    position: sticky;
    top: 0;
    z-index: 10;
    box-shadow: 0 1px 0 rgba(148, 163, 184, 0.25);
}

.boundary-detail-table th {
    padding: 14px 12px;
    text-align: center;
    font-weight: 700;
    font-size: 11px;
    color: #334155;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    border-bottom: 1px solid #e2e8f0;
    background: #f8fafc;
}

.boundary-detail-table tbody {
    background: #ffffff;
}

.boundary-detail-table tbody tr:first-child {
    border-top: none;
}

.boundary-detail-table th.th-actions {
    text-align: center;
    width: 120px;
}

.boundary-detail-table tbody tr {
    border-bottom: 1px solid #edf2f7;
    transition:
        background-color 0.15s ease,
        box-shadow 0.15s ease;
    cursor: pointer;
    background: #ffffff;
}

.boundary-detail-table tbody tr:nth-child(even) {
    background: #fbfcfe;
}

.boundary-detail-table tbody tr:hover {
    background: #f6f8fb;
}

.boundary-detail-table tbody tr:last-child {
    border-bottom: none;
}

.boundary-detail-table td {
    padding: 14px 12px;
    vertical-align: middle;
    text-align: center;
    color: #1f2937;
    font-size: 12.5px;
}

.boundary-detail-table .td-name {
    font-weight: 600;
    color: #0f172a;
    /* min-width: 180px; */
}

.boundary-detail-table .td-method,
.boundary-detail-table .td-facility,
.boundary-detail-table .td-share,
.boundary-detail-table .td-category,
.boundary-detail-table .td-worker,
.boundary-detail-table .td-period {
    color: #64748b;
    font-weight: 400;
}

.boundary-detail-table .td-period {
    white-space: nowrap;
    min-width: 170px;
}

.boundary-detail-table .td-scope {
    min-width: 120px;
}

.boundary-detail-table .td-status {
    text-align: center;
    width: 90px;
}

.boundary-detail-table .td-actions {
    text-align: center;
    padding: 12px 20px;
}

.action-buttons {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    justify-content: center;
}

.status-badge {
    display: inline-block;
    padding: 4px 10px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
    background: #f8fafc;
    color: #64748b;
    border: 1px solid #e5e7eb;
    text-transform: none;
    letter-spacing: 0;
    white-space: nowrap;
}

.status-badge.ACTIVE {
    background: #eefbf4;
    color: #1f7a4d;
    border-color: #bfe8d1;
}

.status-badge.draft {
    background: #fff7ed;
    color: #9a5800;
    border-color: #f5d7b0;
}

.status-badge-inactive {
    background: #f8fafc;
    color: #94a3b8;
    border: 1px solid #e2e8f0;
}

.scope-stack {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    justify-content: center;
}

.scope-pill {
    display: inline-block;
    padding: 4px 10px;
    border-radius: 12px;
    font-size: 10px;
    font-weight: 600;
    border: 1px solid;
    background: #f8fafc;
    color: #64748b;
    border-color: #e2e8f0;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    white-space: nowrap;
}

.scope-pill.muted {
    background: #f1f5f9;
    color: #cbd5e1;
    border-color: #e2e8f0;
}

.board-empty {
    padding: 60px 20px;
    text-align: center;
    color: #94a3b8;
}

.board-empty p {
    margin: 0;
    font-size: 13px;
    font-weight: 400;
    color: #94a3b8;
    letter-spacing: 0.2px;
}

/* 조직 상세 정보 모달 */
.org-detail-content {
    padding: 4px 0;
}

.org-detail-layout {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
    align-items: start;
}

.org-detail-column {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.org-detail-section-compact {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.detail-section-title-compact {
    margin: 0;
    font-size: 12px;
    font-weight: 700;
    color: #0f172a;
    padding-bottom: 6px;
    border-bottom: 1px solid #e2e8f0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.org-detail-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.org-detail-row {
    display: grid;
    grid-template-columns: 100px 1fr;
    gap: 12px;
    align-items: start;
    padding: 6px 0;
    border-bottom: 1px solid #f1f5f9;
}

.org-detail-row:last-child {
    border-bottom: none;
}

.org-detail-label {
    font-size: 11px;
    font-weight: 600;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    flex-shrink: 0;
}

.org-detail-value {
    font-size: 13px;
    font-weight: 500;
    color: #0f172a;
    word-break: break-word;
    text-align: left;
}

.org-detail-remarks-compact {
    padding: 10px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
}

.org-detail-remarks-compact p {
    margin: 0;
    font-size: 12px;
    color: #475569;
    line-height: 1.5;
}

.boundary-detail-modal-body {
    padding: 0;
}

.boundary-detail-modal-content {
    padding: 20px 24px 24px;
}

.boundary-detail-section {
    margin-bottom: 24px;
}

.boundary-detail-section:last-child {
    margin-bottom: 0;
}

.detail-section-title {
    margin: 0 0 12px 0;
    font-size: 14px;
    font-weight: 700;
    color: #0f172a;
    padding-bottom: 8px;
    border-bottom: 2px solid #e2e8f0;
}

.detail-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 12px;
}

.detail-remarks {
    margin: 0;
    padding: 12px;
    background: #f8fafc;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    color: #475569;
    line-height: 1.6;
    font-size: 13px;
}

.facility-table-wrapper {
    width: 100%;
    overflow-x: auto;
    max-height: 300px;
    overflow-y: auto;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
}

.facility-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    background: #ffffff;
    font-size: 13px;
}

.facility-table thead {
    background: #fafbfc;
    position: sticky;
    top: 0;
    z-index: 10;
}

.facility-table th {
    padding: 10px 12px;
    text-align: center;
    font-weight: 500;
    font-size: 11px;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    border-bottom: 1px solid #e2e8f0;
    background: #fafbfc;
}

.facility-table tbody tr {
    border-bottom: 1px solid #f1f5f9;
    transition: background-color 0.15s ease;
}

.facility-table tbody tr:hover {
    background: #fafbfc;
}

.facility-table tbody tr:last-child {
    border-bottom: none;
}

.facility-table td {
    padding: 12px;
    text-align: center;
    vertical-align: middle;
    color: #1e293b;
    font-size: 12px;
}

.facility-name-cell {
    font-weight: 500;
    color: #0f172a;
}

.facility-code-cell {
    color: #64748b;
    font-weight: 400;
}

.facility-type-cell {
    color: #64748b;
    font-weight: 400;
}

.facility-address-cell {
    color: #64748b;
    font-weight: 400;
    text-align: left;
}

.facility-address-text {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    line-height: 1.4;
}

.facility-address-text i {
    font-size: 10px;
    color: #94a3b8;
    margin-top: 2px;
    flex-shrink: 0;
}

.category-list-modal {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    max-height: 300px;
    overflow-y: auto;
    padding: 4px;
}

.category-chip-modal {
    display: inline-flex;
    align-items: center;
    padding: 6px 12px;
    font-size: 12px;
    font-weight: 500;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    color: #475569;
    transition: all 0.15s ease;
}

.category-chip-modal:hover {
    background: #f1f5f9;
    border-color: #cbd5e1;
}

.detail-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 8px 10px;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    transition: all 0.15s ease;
}

.detail-item:hover {
    border-color: #cbd5e1;
    background: #fafbfc;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
}

.detail-label {
    font-size: 10px;
    font-weight: 600;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    margin-bottom: 2px;
}

.detail-value {
    font-size: 12px;
    font-weight: 500;
    color: #1e293b;
    line-height: 1.4;
    word-break: break-word;
}

/* 추가 버튼 */
.add-boundary-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 10px 16px;
    border: 1px dashed #cbd5e1;
    background: #ffffff;
    color: #64748b;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
    font-size: 12px;
    font-weight: 600;
    margin-top: 8px;
}

.add-boundary-btn:hover {
    background: #f8fafc;
    border-color: #94a3b8;
    color: #475569;
    border-style: solid;
}

/* 상태 토글 버튼 */
.status-toggle-btn {
    background: transparent;
    border: none;
    cursor: pointer;
    color: #cbd5e1;
    font-size: 18px;
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: color 0.15s ease;
    outline: none;
    border-radius: 0;
}

.status-toggle-btn:hover {
    background: transparent;
    color: #94a3b8;
}

.status-toggle-btn.ACTIVE {
    color: #059669;
}

.status-toggle-btn.small {
    font-size: 16px;
}

.status-toggle-btn:active,
.status-toggle-btn:focus {
    outline: none;
    border: none;
}

/* 빈 상태 */
.empty-state {
    padding: 60px 20px;
    text-align: center;
    color: #94a3b8;
}

.empty-state i {
    font-size: 36px;
    margin-bottom: 12px;
    color: #cbd5e1;
}

.empty-state p {
    margin: 0;
    font-size: 13px;
    font-weight: 500;
}

/* 반응형 */
@media (max-width: 1024px) {
    .master-detail-layout {
        flex-direction: column;
    }

    .master-panel {
        width: 100%;
        min-width: 0;
        max-height: 300px;
        border-right: none;
        border-bottom: 1px solid #e2e8f0;
    }

    .detail-panel {
        flex: 1;
        min-height: 400px;
    }

    .detail-body {
        padding: 16px;
    }

    .boundary-tabs ::v-deep .nav {
        flex-wrap: wrap;
    }
}

@media (max-width: 768px) {
    .master-panel {
        max-height: 250px;
    }

    .master-item {
        padding: 10px 12px;
    }

    .master-item-name {
        font-size: 12px;
    }

    .detail-header {
        padding: 12px 16px;
    }

    .detail-title {
        font-size: 16px;
    }

    .detail-body {
        padding: 12px;
        gap: 12px;
    }
}
</style>
